import styled from 'styled-components'
import { Button as SutraButton } from 'sutra/components/Button'
import { Checkbox } from 'sutra/components/Checkbox/Checkbox'
import { Input as SutraInput } from 'sutra/components/Input/Input'
import { accents } from 'sutra/tokens/colors'

// Shared layout primitives for the popover's in-pane editors (Keywords,
// Smart search, Records, Sentences, Rating). Kept out of inline
// `style={{...}}` per the `no-inline-styles` rule — a fresh object per
// render defeats memoization and can't be themed.

// Sutra's checkbox renders at 18x18 by default; these filter rows are dense,
// so we shrink the box to 14x14. The checkmark is sized from the box, so it
// scales with it. Shared across the popover's field list (verified/select
// checkboxes) and the Rating pane's null-rows checkbox.
export const SmallCheckbox = styled(Checkbox)`
  width: 14px;
  height: 14px;
`

export const PaneContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`

export const ScrollablePaneContainer = styled(PaneContainer)`
  overflow: auto;
`

export const PaneHeading = styled.h3`
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
`

export const PaneDescription = styled.p`
  margin: 0 0 8px 0;
  font-size: 12px;
  color: #6b7280;
`

export const PaneRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

// Hover styling lives in CSS instead of a React `hoveredValue` useState — so
// moving the mouse between rows does not re-render the popover tree. `$active`
// carries the `isChecked || isHighlighted` selected background (the stronger
// tint); `&:hover` paints the lighter tint when the row is hovered and isn't
// already active, so hover and selected read as visibly different states.
// The `.row-action-btn` child slot ("only" buttons, delete buttons) becomes
// visible on row hover via `&:hover .row-action-btn`. Shared across every
// value-list row in the popover (field lists, saved presets, Sentiment's
// categorical checkboxes).
export const HoverableValueRow = styled.div<{ $active: boolean }>`
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ $active }) =>
    $active ? accents.reflectionBlue[100] : 'transparent'};
  transition: background-color 0.2s ease;
  ${({ $active }) =>
    $active
      ? ''
      : `&:hover {
          background-color: ${accents.reflectionBlue[100]};
        }`}
  & .row-action-btn {
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease;
  }
  &:hover .row-action-btn {
    opacity: 1;
    visibility: visible;
  }
`

// Padding (not margin) so the space survives even when the pane's content
// overflows the popover's scrolling container — a scrolled flex column drops
// the ancestor's own block-end padding, but padding that belongs to this row
// itself doesn't depend on that.
export const PaneFooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px;
`

export const KeywordRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

export const KeywordRowRemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  &:hover {
    background-color: #fee2e2;
    color: #ef4444;
  }
`

export const KeywordAddMoreButton = styled(SutraButton)`
  width: 100%;
`

// Sutra's Select forwards no `className`, so it can't be `styled(Select)` —
// this wraps it to pin the 190px width the antd Select used to carry itself.
export const NumericOperatorSelectWrap = styled.div`
  width: 190px;
  flex-shrink: 0;
`

// Sort's trigger label stays fixed ("Sort") regardless of selection, so the
// trigger itself can stay narrow — width comes from this wrapper, not the
// panel (which grows to fit the longest option label).
export const SortSelectWrap = styled.div`
  width: 104px;
  flex-shrink: 0;
`

// A row/section that greys out and stops accepting input while its
// exclusive counterpart is active (Sentiment's categorical-vs-numeric
// mutual exclusivity).
export const PaneExclusiveGroup = styled.div<{ $disabled: boolean }>`
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  ${({ $disabled }) =>
    $disabled && 'pointer-events: none; cursor: not-allowed;'}
`

// The small "only" affordance on a value row — revealed on row hover via
// `HoverableValueRow`'s `.row-action-btn` slot (pass `className="row-action-btn"`).
export const PaneRowOnlyButton = styled.button`
  font-size: 11px;
  color: #6b7280;
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
  font-weight: 500;
  width: 28px;
  &:hover {
    background-color: #e5e7eb;
    color: #374151;
  }
`

// The delete affordance on the saved-filters list row — same
// `.row-action-btn` reveal slot as `PaneRowOnlyButton`, red hover instead of
// grey (a destructive action, not a navigation one).
export const PaneRowDeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  border-radius: 3px;
  color: #6b7280;
  &:hover {
    background-color: #fee2e2;
    color: #ef4444;
  }
`

export const PaneValueLabel = styled.span`
  font-size: 13px;
  color: #1f2937;
`

export const NumericValueInput = styled(SutraInput)`
  width: 90px;
  font-size: 12px;
`

// "Type" / "Values" section eyebrows — shared by Rating and Sentiment, whose
// panes both organize into a type-selector section and a values section.
export const PaneEyebrow = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`

// The type-selector radio-card shape — shared by Rating (RatingTypeSelector)
// and Sentiment (its Sentence/Review Sentiment cards).
export const PaneTypeCard = styled.div<{
  $selected: boolean
  $disabled?: boolean
}>`
  padding: 8px 12px;
  margin-bottom: 4px;
  border-radius: 6px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  border: 1px solid
    ${({ $selected }) =>
      $selected ? accents.reflectionBlue[500] : 'transparent'};
  background-color: ${({ $selected }) =>
    $selected ? accents.reflectionBlue[100] : 'transparent'};
`

export const PaneTypeCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const PaneRadioInput = styled.input`
  cursor: pointer;
  flex-shrink: 0;
`

export const PaneTypeCardLabel = styled.span<{ $selected: boolean }>`
  font-size: 13px;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  color: #1f2937;
`

export const PaneTypeCardBody = styled.div`
  padding-left: 24px;
`

export const PaneTypeCardDescription = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 6px 0;
`

// Rating-only — the greyscale-when-inactive preview-card row under each type
// option. Sentiment's type cards have no preview images.
export const RatingTypeCardPreviewRow = styled.div`
  display: flex;
  gap: 10px;
`

export const RatingTypeCardPreviewImage = styled.img<{ $active: boolean }>`
  width: 80px;
  height: auto;
  opacity: ${({ $active }) => ($active ? 1 : 0.85)};
  filter: ${({ $active }) =>
    $active ? 'none' : 'grayscale(100%) brightness(0.8) contrast(1.1)'};
  transition:
    opacity 0.15s ease,
    filter 0.15s ease;
`

export const PaneDivider = styled.div`
  height: 1px;
  background-color: #e5e7eb;
  margin: 12px 0 8px;
`

// The "── OR ──" separator between Sentiment's categorical and numeric
// sections.
export const PaneOrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 0;
  color: #9ca3af;
  font-size: 12px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: #e5e7eb;
  }
`

export const RatingSliderWrap = styled.div`
  padding: 4px 40px 16px;
`

export const RatingNullRow = styled.div`
  padding: 8px 20px;
  display: flex;
  align-items: center;
  cursor: pointer;
`

export const RatingNullCheckbox = styled(SmallCheckbox)`
  margin-right: 8px;
`

export const RatingNullLabel = styled.span`
  font-size: 14px;
  color: #1f2937;
`

// origin/main carried these inline on each sentiment row (`padding: '4px
// 8px', marginBottom: '4px'`, checkbox `marginRight: '12px'`); the extracted
// HoverableValueRow/PaneRow declare neither.
export const SentimentValueRowContainer = styled(HoverableValueRow)`
  padding: 4px 8px;
  margin-bottom: 4px;
`

export const SentimentValueRowLeft = styled(PaneRow)`
  gap: 12px;
`
